import { createColumnHelper } from '@tanstack/react-table';
import { t } from 'i18next';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';

import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import { IntegrationIcon } from '@/components/organisms/Integrations';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  BroadcastSortDirection,
  FilterStatus,
} from '@/modules/broadcast/contexts/BroadcastFilterContext';
import { Broadcast } from '@/shared/types/broadcast';
import { COLUMN_PRESETS } from '@/util/constants';
import { clampPercentage } from '@/util/limits';

import ActionsCell from './ActionsCell';

interface ColumnProps {
  sortDirection: BroadcastSortDirection;
  toggleSortDirection: () => void;
  onDeleteClick?: (broadcast: string) => void;
  canWrite?: boolean;
}

type Status = FilterStatus | 'scheduled';

const getStatusBadge = (status: Status) => {
  switch (status) {
    case 'sent':
      return (
        <Badge type="secondary" flavour="successful">
          {t(`broadcasts.status.${status}`)}
        </Badge>
      );
    case 'draft':
      return (
        <Badge type="secondary" flavour="neutral">
          {t(`broadcasts.status.${status}`)}
        </Badge>
      );
    case 'ready':
      return (
        <Badge type="secondary" flavour="neutral">
          {t(`broadcasts.status.${status}`)}
        </Badge>
      );
    default:
      return (
        <Badge type="secondary" flavour="neutral">
          {t(`broadcasts.status.${status}`)}
        </Badge>
      );
  }
};

const columnHelper = createColumnHelper<Broadcast>();

export const columns = ({
  sortDirection,
  toggleSortDirection,
  onDeleteClick,
  canWrite = false,
}: ColumnProps) => [
  columnHelper.accessor('channel', {
    header: () => '',
    cell: (info) => {
      const channel = info.getValue();
      return (
        <div className="flex w-full items-center justify-center">
          <IntegrationIcon type={channel} />
        </div>
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  }),
  columnHelper.accessor('name', {
    header: () => <span className="pl-2">{t('broadcasts.title')}</span>,
    cell: (info) => (
      <div className="flex min-w-0 items-center gap-3 pl-2">
        <span className="min-w-0 truncate font-medium">{info.getValue()}</span>
        <RowActionIndicator action="view" />
      </div>
    ),
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
  }),
  columnHelper.accessor('status', {
    header: () => t('broadcasts.status_title'),
    cell: (info) => {
      const s = info.getValue();
      return getStatusBadge(s);
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  }),
  columnHelper.accessor('created', {
    header: () => (
      <button
        type="button"
        onClick={toggleSortDirection}
        className={cn(
          'inline-flex items-center gap-2',
          'text-sm font-medium whitespace-nowrap text-muted-foreground',
          'transition-colors hover:text-foreground',
          'rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none'
        )}
        aria-label={t('table.sort_by', { 0: t('broadcasts.created') })}
      >
        {t('broadcasts.created')}
        {sortDirection === 'desc' ? (
          <ArrowDownIcon size={16} aria-hidden="true" />
        ) : (
          <ArrowUpIcon size={16} aria-hidden="true" />
        )}
      </button>
    ),
    cell: (info) => {
      const d = info.getValue();
      const date = new Date(d);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      };
      return (
        <div className="mono text-sm leading-5">
          {new Intl.DateTimeFormat('en-US', options).format(date)}
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  }),
  columnHelper.display({
    id: 'views',
    header: () => t('broadcasts.views'),
    cell: (info) => {
      const { read_count, sent_count } = info.row.original;
      const percentage = clampPercentage(read_count, sent_count);
      return sent_count === 0 ? '-' : `${percentage}% (${read_count})`;
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', isNumeric: true },
  }),
  columnHelper.display({
    id: 'interactions',
    header: () => t('broadcasts.interactions'),
    cell: (info) => {
      const { replied_count, sent_count } = info.row.original;
      const percentage = clampPercentage(replied_count, sent_count);
      return sent_count === 0 ? '-' : `${percentage}% (${replied_count})`;
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', isNumeric: true },
  }),
  columnHelper.display({
    id: 'actions',
    header: () => '',
    cell: (info) => {
      const broadcast = info.row.original;
      return (
        <ActionsCell broadcast={broadcast} onDeleteClick={onDeleteClick} canWrite={canWrite} />
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  }),
];
